import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import type { RoleRecord, StaffMember } from '@/types/portal';

interface ChangeRoleModalProps {
  member: StaffMember | null;
  roles: RoleRecord[];
  /** The member is the agency's only admin, so they can't be moved off Admin. */
  lastAdmin: boolean;
  saving: boolean;
  error?: string;
  onClose: () => void;
  onSave: (member: StaffMember, roleId: string) => void;
}

export default function ChangeRoleModal({
  member,
  roles,
  lastAdmin,
  saving,
  error = '',
  onClose,
  onSave,
}: ChangeRoleModalProps) {
  const [roleId, setRoleId] = useState('');

  useEffect(() => {
    if (member) setRoleId(member.roleId);
  }, [member]);

  const blocked = lastAdmin && roleId !== 'admin';
  const unchanged = roleId === member?.roleId;

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      icon="ri-shield-user-line"
      title="Change role"
      subtitle={member ? `${member.name} · ${member.email}` : undefined}
      widthClassName="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="ri-save-line"
            disabled={!member || unchanged || blocked || saving}
            onClick={() => member && onSave(member, roleId)}
          >
            {saving ? 'Saving…' : 'Save role'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800"
          >
            <i className="ri-error-warning-line mt-0.5" />
            <span>{error}</span>
          </div>
        ) : null}
        <div>
          <label htmlFor="change-role" className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
            Role
          </label>
          <div className="relative">
            <select
              id="change-role"
              value={roleId}
              onChange={(event) => setRoleId(event.target.value)}
              className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
          {blocked ? (
            <p className="mt-1.5 text-[11px] text-primary-700">
              {member?.name} is the only admin. Make someone else an admin first.
            </p>
          ) : roleId === 'admin' && !unchanged ? (
            <p className="mt-1.5 text-[11px] text-primary-700">
              Admins have every permission, including sending payments and managing users.
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
